import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getTracking, fileUrl } from "../api";
import Header from "../components/Header";
import { MapPin, Clock, ShieldCheck, Camera, PenLine, CheckCircle2, Loader2, Building2, ListChecks } from "lucide-react";

const fmt = (x) => (x ? new Date(x).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }) : "—");

const MapEmbed = ({ gps, label }) => {
  if (!gps || !gps.lat) return null;
  const { lat, lng } = gps;
  const bbox = `${lng - 0.006}%2C${lat - 0.004}%2C${lng + 0.006}%2C${lat + 0.004}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
  return (
    <div className="rounded-xl overflow-hidden border border-neutral-200">
      <iframe title={label} src={src} className="w-full h-48" loading="lazy" />
    </div>
  );
};

const Suivi = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getTracking(token).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-5">
        <div className="text-center">
          <ShieldCheck size={40} className="mx-auto text-neutral-300 mb-3" />
          <p className="text-neutral-600">Ce lien de suivi n'est pas disponible.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-neutral-100 flex items-center justify-center"><Loader2 className="animate-spin text-brand" /></div>;
  }

  const done = data.status === "terminee";

  return (
    <div className="min-h-screen bg-neutral-100 font-body pb-12" data-testid="suivi-page">
      <Header solid />
      <div className="bg-neutral-950 text-white">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center gap-1.5 text-sm text-neutral-300" data-testid="suivi-logo"><ShieldCheck size={16} className="text-brand" /> Suivi sécurisé de prestation</div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8 space-y-5">
        <div className="bg-white rounded-2xl border border-neutral-200 p-6">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            <CheckCircle2 size={13} /> {done ? "Prestation réalisée" : "Prestation en cours"}
          </span>
          <h1 className="font-heading font-extrabold text-2xl text-neutral-900 mt-3">{data.prestation || "Prestation de nettoyage"}</h1>
          <p className="text-neutral-600 flex items-center gap-1.5 mt-1"><Building2 size={15} /> {data.client_nom}</p>
          <p className="text-neutral-500 flex items-center gap-1.5 mt-0.5"><MapPin size={15} /> {[data.adresse, data.ville].filter(Boolean).join(", ") || "—"}</p>
          {(data.collaborateurs?.length > 0 || data.collaborateur_nom) && (
            <p className="text-sm text-neutral-500 mt-2">
              {data.collaborateurs?.length > 1 ? "Intervenants" : "Intervenant"} :{" "}
              <b className="text-neutral-700">
                {(data.collaborateurs?.length > 0 ? data.collaborateurs : [data.collaborateur_nom]).join(", ")}
              </b>
            </p>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-6">
          <h2 className="font-heading font-bold text-neutral-900 mb-4">Horodatage & géolocalisation</h2>
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800"><Clock size={15} className="text-brand" /> Début : {fmt(data.start_at)}</div>
              {data.start_gps?.lat && <p className="text-xs text-emerald-600 mt-1 ml-6">📍 Position confirmée sur site</p>}
              <div className="mt-2 ml-6"><MapEmbed gps={data.start_gps} label="Début" /></div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800"><Clock size={15} className="text-emerald-600" /> Fin : {fmt(data.end_at)}</div>
              {data.duration && <p className="text-xs text-neutral-500 mt-1 ml-6">Durée : {data.duration}</p>}
            </div>
          </div>
        </div>

        {/* Photos */}
        {(data.photos_avant?.length > 0 || data.photos_apres?.length > 0) && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6">
            <h2 className="font-heading font-bold text-neutral-900 mb-4 flex items-center gap-2"><Camera size={17} className="text-brand" /> Photos de la prestation</h2>
            {data.photos_avant?.length > 0 && (
              <>
                <p className="text-xs font-semibold text-neutral-500 mb-2">AVANT</p>
                <div className="grid grid-cols-3 gap-2 mb-4">{data.photos_avant.map((p) => (
                  <div key={p}><img src={fileUrl(p)} alt="avant" className="w-full h-24 object-cover rounded-lg border border-neutral-200" />{data.captions?.[p] && <p className="text-xs text-neutral-500 mt-1">{data.captions[p]}</p>}</div>
                ))}</div>
              </>
            )}
            {data.photos_apres?.length > 0 && (
              <>
                <p className="text-xs font-semibold text-neutral-500 mb-2">APRÈS</p>
                <div className="grid grid-cols-3 gap-2">{data.photos_apres.map((p) => (
                  <div key={p}><img src={fileUrl(p)} alt="après" className="w-full h-24 object-cover rounded-lg border border-neutral-200" />{data.captions?.[p] && <p className="text-xs text-neutral-500 mt-1">{data.captions[p]}</p>}</div>
                ))}</div>
              </>
            )}
          </div>
        )}

        {/* Tâches effectuées */}
        {data.tasks?.length > 0 && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6" data-testid="suivi-tasks">
            <h2 className="font-heading font-bold text-neutral-900 mb-4 flex items-center gap-2"><ListChecks size={17} className="text-brand" /> Tâches effectuées</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {data.tasks.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-neutral-700">
                  <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> {t}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signature + note */}
        {(data.signature || data.note) && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6">
            {data.signature && (
              <div className="mb-4">
                <h2 className="font-heading font-bold text-neutral-900 mb-2 flex items-center gap-2"><PenLine size={17} className="text-brand" /> Signature du client</h2>
                <img src={fileUrl(data.signature)} alt="signature" className="h-24 border border-neutral-200 rounded-lg bg-white" />
              </div>
            )}
            {data.note && <p className="text-sm text-neutral-600 italic">« {data.note} »</p>}
          </div>
        )}

        <p className="text-center text-xs text-neutral-400 flex items-center justify-center gap-1.5">
          <ShieldCheck size={13} /> Suivi transparent garanti par PTITSERVICES-PRO
        </p>
      </div>
    </div>
  );
};

export default Suivi;
